import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Dialog, InputBase, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchMarketSymbols } from '../features/markets/marketsSlice';
import { visualStrategiesApi } from '../api/visualStrategies';
import { customEventsApi } from '../api/customEvents';

// ⌘K / Ctrl+K: jump to a page, a market's chart, a strategy or a custom event (DWLF-299).
// Symbols come from the store; strategies and custom events are fetched into the palette's own state,
// unfiltered — the Strategies / Custom Events pages keep filtered, paginated lists in their slices, which
// the palette must neither search (it would miss most) nor overwrite.
const LIST_LIMIT = 500;
const listOf = (res, key) => {
  const data = res?.data || res;
  return Array.isArray(data?.[key]) ? data[key] : Array.isArray(data) ? data : [];
};

const PAGES = [
  ['Today', '/today'], ['Cycle clock', '/markets'], ['Markets overview', '/markets/overview'], ['Cycle screener', '/screener'], ['COT (Silver)', '/markets/cot'],
  ['Trade journal', '/trades'], ['Decision quality', '/trades/decisions'], ['Skips · counterfactual', '/trades/counterfactual'], ['Trade plans', '/trade-plans'], ['Trade analytics', '/trades/analytics'],
  ['Position size', '/tools/position-size'], ['Strategy signals', '/my-signals'], ['Events explorer', '/signals/events'],
  ['Activity feed', '/activity'], ['Alert log', '/alerts/log'], ['Custom events', '/custom-events'],
  ['New custom event', '/custom-events/new'], ['Strategies', '/strategies'], ['New strategy', '/strategies/new'],
  ['Strategy templates', '/strategies/templates'], ['Backtests', '/backtests'], ['State machines', '/state-machines'],
  ['Learn', '/learn'], ['Settings', '/settings'],
].map(([label, path]) => ({ kind: 'Page', label, path }));

const MAX_RESULTS = 12;

const scoreText = (text, q) => {
  const l = text.toLowerCase();
  if (l === q) return 0;
  if (l.startsWith(q)) return 1;
  if (l.split(/[\s/._-]+/).some((w) => w.startsWith(q))) return 2;
  return l.includes(q) ? 3 : -1;
};

// A market matches on its ticker or its name ("gold" → XAU/USD). Ties break on the length of the text
// that matched, so "Gold Spot" outranks "Goldman Sachs" even though GS is the shorter ticker.
const score = (item, q) => [item.label, item.name]
  .filter(Boolean)
  .map((text) => ({ s: scoreText(text, q), len: text.length }))
  .filter((m) => m.s >= 0)
  .sort((a, b) => a.s - b.s || a.len - b.len)[0] || { s: -1 };

// Only while signed in: the palette is not rendered otherwise, so the hotkey would swallow the
// browser's own ⌘K and leave the palette 'open' to pop up after login.
export const useCommandPaletteHotkey = (setOpen, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen, enabled]);
};

export default function CommandPalette({ open, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const symbols = useSelector((s) => s.markets.symbols);
  const symbolNames = useSelector((s) => s.markets.symbolNames);
  const [strategies, setStrategies] = useState([]);
  const [customEvents, setCustomEvents] = useState([]);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);

  // Fresh query each time it opens. Only `open` may drive this: the lists below change while the
  // palette is open (their fetches resolve) and must not wipe what the user is typing.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCursor(0);
  }, [open]);

  // Refreshed on every open, so new strategies and events are findable and a failed fetch is retried;
  // the last good list stays searchable meanwhile. fetchMarketSymbols skips a fresh list on its own.
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setLoadFailed(false);
    dispatch(fetchMarketSymbols());
    const load = (request, key, set) => request
      .then((res) => { if (live) set(listOf(res, key)); })
      .catch(() => { if (live) setLoadFailed(true); });
    load(visualStrategiesApi.listStrategies({ limit: LIST_LIMIT }), 'strategies', setStrategies);
    load(customEventsApi.listEvents({ limit: LIST_LIMIT }), 'events', setCustomEvents);
    return () => { live = false; };
  }, [open, dispatch]);

  const items = useMemo(() => [
    // URL form used across the app: XAU/USD → XAU-USD
    ...(symbols || []).map((sym) => ({
      kind: 'Chart', label: sym, name: symbolNames?.[sym], path: `/markets/${sym.replace('/', '-')}`,
    })),
    ...PAGES,
    ...(Array.isArray(strategies) ? strategies : []).map((st) => ({
      kind: 'Strategy', label: st.name || st.strategyId, path: `/strategies/${st.strategyId}/view`,
    })),
    ...(Array.isArray(customEvents) ? customEvents : []).map((ev) => ({
      kind: 'Custom event', label: ev.name || ev.eventId, path: `/custom-events/${ev.eventId}/view`,
    })),
  ], [symbols, symbolNames, strategies, customEvents]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PAGES.slice(0, 8);
    return items
      .map((it) => ({ it, ...score(it, q) }))
      .filter((r) => r.s >= 0)
      .sort((a, b) => a.s - b.s || a.len - b.len)
      .slice(0, MAX_RESULTS)
      .map((r) => r.it);
  }, [items, query]);

  const go = (item) => {
    if (!item) return;
    onClose();
    navigate(item.path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(results.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[cursor]); }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { alignSelf: 'flex-start', mt: '12vh', backgroundColor: 'var(--dwlf-surface)', border: '1px solid var(--dwlf-ink-12)' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, height: 56, borderBottom: '1px solid var(--dwlf-ink-08)' }}>
        <Search sx={{ color: 'var(--dwlf-ink-50)' }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Jump to market (ticker or name), page, strategy, event"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
          onKeyDown={onKeyDown}
          inputProps={{ 'aria-label': 'Search markets, pages, strategies and events' }}
          sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.95rem', color: 'var(--dwlf-text)' }}
        />
        <Typography sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.65rem', letterSpacing: '0.12em', color: 'var(--dwlf-ink-45)', border: '1px solid var(--dwlf-ink-15)', px: 0.75 }}>
          ESC
        </Typography>
      </Box>
      <Box role="listbox" sx={{ maxHeight: '50vh', overflowY: 'auto', py: 0.5 }}>
        {loadFailed && (
          <Typography role="status" sx={{ px: 2, pt: 1.5, fontSize: '0.75rem', color: 'var(--dwlf-warn)' }}>
            Couldn't refresh strategies or events; showing what was last loaded.
          </Typography>
        )}
        {results.length === 0 && (
          <Typography sx={{ px: 2, py: 2, fontSize: '0.85rem', color: 'var(--dwlf-ink-50)' }}>No matches.</Typography>
        )}
        {results.map((r, i) => (
          <ButtonBase
            key={`${r.kind}:${r.path}`}
            role="option"
            aria-selected={i === cursor}
            onClick={() => go(r)}
            onMouseEnter={() => setCursor(i)}
            sx={{
              width: '100%', height: 44, px: 2, display: 'flex', justifyContent: 'space-between', gap: 2,
              backgroundColor: i === cursor ? 'var(--dwlf-accent-10)' : 'transparent',
              borderLeft: `2px solid ${i === cursor ? 'var(--dwlf-accent)' : 'transparent'}`,
            }}
          >
            <Typography noWrap sx={{ fontSize: '0.875rem', color: 'var(--dwlf-text)' }}>
              {r.label}
              {r.name && <Box component="span" sx={{ color: 'var(--dwlf-ink-50)' }}>{` ${r.name}`}</Box>}
            </Typography>
            <Typography sx={{ flexShrink: 0, fontFamily: '"Space Mono", monospace', fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--dwlf-ink-50)' }}>
              {r.kind}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </Dialog>
  );
}
